import { createSelector } from "@reduxjs/toolkit";
import { selectLanguagesData } from "./languageSelectors";
import {
  selectChapters,
  selectCurrentPage,
  selectItemsPerPage,
  selectSearchQuery,
  selectSelectedLanguages,
  selectSyncChapters,
} from "./filterSelectors";

export const selectSentences = (state) => state.sentences;
export const selectSentencesData = (state) => state.sentences.sentences;
export const selectSentencesLoading = (state) => state.sentences.loading;
export const selectSentencesError = (state) => state.sentences.error;
export const selectAudioProgress = (state) => state.sentences.audioProgress;
export const selectIsAudioPlaying = (state) => state.sentences.isAudioPlaying;
export const selectHighlightedColors = (state) =>
  state.sentences.highlightedColors;
export const selectSentencesLoadingIds = (state) =>
  state.sentences.sentencesLoadingIds;
export const selectSentencesCurrentBatch = (state) =>
  state.sentences.currentBatch;

export const selectCurrentSentences = (bookId, chapterId) =>
  createSelector(
    [
      selectSentencesData,
      selectCurrentPage,
      selectItemsPerPage,
      selectSearchQuery,
      selectSentencesCurrentBatch,
    ],
    (sentences, currentPage, itemsPerPage, searchQuery, currentBatch) => {
      // Filter sentences for the given book and chapter
      let filteredSentences;
      if (searchQuery.length) {
        // Find all sentence_counters that match the search
        const matchingSentences = sentences.filter((item) => {
          const match = item.text
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase());
          if (match) console.log("Matched sentence:", item);
          return match;
        });

        const matchingCounters = new Set(
          matchingSentences.map((item) => {
            console.log("Extracting sentence_counter:", item.sentence_counter);
            return item.sentence_counter;
          })
        );
        filteredSentences = sentences.filter(
          (item) =>
            item.book_id === bookId &&
            (matchingCounters.has(item.sentence_counter) ||
              item.text.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      } else {
        filteredSentences = sentences.filter(
          (item) => item.book_id === bookId && item.chapter_id === chapterId
        );
      }

      // Calculate pagination range
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;

      const foundSentences = filteredSentences.slice(startIndex, endIndex);
      // Return only the sentences for the current page
      if (foundSentences.length) return foundSentences;

      // If not found normally, return currentBatch of sentences for the book
      if (!currentBatch.length) return [];
      const currentBatchSentences = currentBatch.filter(
        (item) => item.book_id === bookId
      );
      return currentBatchSentences;
    }
  );

// Selector to check if the current language has audio
export const selectHasAudio = (selectedLang) =>
  createSelector([selectLanguagesData], (languages) => {
    const currentLanguage = languages.find(
      (lang) => lang.name === selectedLang
    );
    return currentLanguage?.name.toLowerCase().includes("audio") ?? false;
  });

// Selector to check if a book is highlighted
export const selectIsHighlighted = (bookId) =>
  createSelector([selectHighlightedColors], (highlightedColors) =>
    highlightedColors.includes(bookId)
  );

export const selectIsSentenceFetched = (bookId, chapterId) => {
  let isFetched = false,
    excludeIds = [];
  createSelector(
    [
      selectSentencesData,
      selectCurrentPage,
      selectItemsPerPage,
      selectSearchQuery,
    ],
    (sentences, currentPage, itemsPerPage, searchQuery) => {
      // Filter sentences belonging to the current chapter
      let sentencesPerChapter = sentences.filter(
        (item) => item.chapter_id === chapterId && item.book_id === bookId
      );

      // If searchQuery exists, filter sentences based on it (search logic can vary)
      if (searchQuery.length) {
        sentencesPerChapter = sentencesPerChapter.filter(
          (sentence) =>
            sentence.text.toLowerCase().includes(searchQuery.toLowerCase()) // assuming sentences have a 'text' property
        );
      }
      // If no sentences are fetched for the chapter, return false (need to fetch)
      if (sentencesPerChapter.length === 0) return { isFetched, excludeIds };

      // Calculate the sentence range for the current page
      const startIdx = (currentPage - 1) * itemsPerPage;
      const endIdx = startIdx + itemsPerPage; // Ensures we check for at least `itemsPerPage` sentences

      // Get the sentences for the current page
      const fetchedSentences = sentencesPerChapter.slice(startIdx, endIdx);

      // Maintain an exclude array of already fetched sentence IDs
      excludeIds = fetchedSentences.map((item) => item.id);

      // Ensure we have at least `itemsPerPage` sentences, else we need to fetch more
      isFetched = fetchedSentences.length >= itemsPerPage;
    }
  );
  return { isFetched, excludeIds };
};
export const selectChapterIds = (state) => {
  const chapters = selectChapters(state);

  return chapters.map((ch) => ch.chapter_id);
};

export const selectSelectedLanguageIds = (state) => {
  const selectedLanguageNames = selectSelectedLanguages(state);
  const selectedLanguageSet = new Set(selectedLanguageNames);

  return state.languages.languages
    .filter((item) => selectedLanguageSet.has(item.name))
    .map((item) => item.id);
};

export const selectSelectedBookIds = (state) => {
  const selectedLanguageIds = selectSelectedLanguageIds(state);

  return state.books.books.filter((item) =>
    selectedLanguageIds.includes(item.language_id)
  );
};

// there is still a bug
//  on prev page, items are still being fetched
export const selectCurrentPageFetchedBookIds = createSelector(
  [
    selectChapters,
    selectChapterIds,
    selectSyncChapters,
    selectSentencesData,
    selectCurrentPage,
    selectItemsPerPage,
    selectSelectedBookIds,
  ],
  (
    chapters,
    chapterIds,
    syncChapters,
    sentences,
    currentPage,
    itemsPerPage,
    selectedBookIds
  ) => {
    let fetchedBookIds;
    if (syncChapters) {
      fetchedBookIds = sentences
        .filter(
          (item) =>
            item.chapter_id === chapterIds[0] && item.page === currentPage
        )
        .map((item) => item.book_id);
    } else {
      fetchedBookIds = chapters
        .map((chapter) =>
          sentences
            .filter(
              (item) =>
                item.chapter_id === chapter.chapter_id &&
                item.book_id === chapter.book_id &&
                item.page === currentPage
            )
            .map((item) => item.book_id)
        )
        .flat();
    }

    const requiredItemsPerBook = currentPage * itemsPerPage;

    // divide sentences per page
    const bookCount = fetchedBookIds.reduce((acc, bookId) => {
      acc[bookId] = (acc[bookId] || 0) + 1;
      return acc;
    }, {});

    // Find the books that are not repeated the required number of times
    const booksWithIncorrectRepetition = Object.keys(bookCount).filter(
      (bookId) => bookCount[bookId] < requiredItemsPerBook
    );

    const fetchBookSet = new Set(fetchedBookIds);

    // if fetchedBookIds doesnt have any item from selectedBookIds, it is needed
    // Find books from selectedBookIds that are not present in fetchedBookIds
    const missingBookIds = selectedBookIds
      .filter((book) => !fetchBookSet.has(book.id))
      .map((book) => book.id);
    // needed books would be missingBookIds and booksWithIncorrectRepetition
    const neededBooks = [...missingBookIds, ...booksWithIncorrectRepetition];

    return neededBooks;
  }
);
